import { readonly, onMounted, computed } from 'vue'
import { defineStore } from 'pinia'
import { useStore } from 'vuex'
import { Component, Entity, useEntityService } from '@/services/entity.service'
import { HTTPGatewayClient } from '@/services/http-client'
import { LngLatBounds } from 'mapbox-gl'
import { useCategoryStoreBase } from './category-store-base'

// --- Types and Helpers ---

export type Category = {
  id: string
  name: string
  color?: string
  level?: number
  path?: string
  parentId?: string
  parents?: Category[]
}

/**
 * Maps an Entity object to a Category object.
 */
export const entityToCategory = (entity: Entity): Category => {
  const pathComponent = entity.components?.find((c) => c.type === 'path')
  const path = pathComponent?.['path']
  const pathSegments = path?.split('.')

  return {
    id: entity.id,
    name: entity.name,
    color: entity.components?.find((c) => c.type === 'label' && c['name'] === 'color')?.['value'],
    level: pathSegments?.length,
    path,
    parentId: pathSegments?.slice(-2, -1)?.[0]
  }
}

// --- Pinia Store Definition ---

export const useCategoryService = defineStore('category', () => {
  const base = useCategoryStoreBase()
  const vuexStore = useStore()

  // Shared in-flight load so concurrent callers await the SAME fetch instead of
  // racing past an already-started (but not yet finished) load and reading an
  // empty tree.
  let inFlight: Promise<void> | null = null

  async function fetchAll(): Promise<void> {
    if (base.isLoaded.value) return
    if (inFlight !== null) return inFlight

    const entityService = useEntityService()
    base.isLoading.value = true

    inFlight = (async () => {
      try {
        const pageable = await entityService.getAll({
          'labels.type': 'CATEGORY',
          size: 5000
        })
        const categories = pageable.content.map(entityToCategory)

        const newCategoriesById = new Map<string, Category>()
        const newCategoriesByPath = new Map<string, Category>()

        for (const category of categories) {
          newCategoriesById.set(category.id, category)
          if (category.path) newCategoriesByPath.set(category.path, category)
        }

        base.categoriesById.value = newCategoriesById
        base.categoriesByPath.value = newCategoriesByPath
        base.isLoaded.value = true
      } catch (error) {
        console.error('Failed to fetch categories:', error)
        base.isLoaded.value = false
        throw error
      } finally {
        base.isLoading.value = false
        inFlight = null
      }
    })()

    return inFlight
  }

  onMounted(() => {
    fetchAll()
  })

  function getParentsById(id: string): Category[] {
    if (!id) return []
    const category = base.categoriesById.value.get(id)
    if (!category?.path) return []
    return base.getParentsByPath(category.path)
  }

  /**
   * Fetches categories with associated assets.
   *
   * When `nextTo` is true: fetches assets in the given viewport/path and derives
   * categories from their BELONGS_TO relations. This mirrors the entity manager's
   * asset query so the category menu is always consistent with what is visible.
   *
   * When `nextTo` is false: fetches the given category IDs directly (used to keep
   * already-selected categories visible even outside the current viewport).
   */
  async function getAllWithAssets(
    nextTo?: boolean,
    assetPath?: string,
    viewport?: LngLatBounds,
    ids?: string[]
  ): Promise<Category[]> {
    const entityService = useEntityService()

    let initialCategories: Category[]

    if (nextTo) {
      // Fetch assets using the same params the entity manager uses, so the
      // category menu always reflects assets that are actually in the viewport.
      const viewportString = viewport ? viewport.toArray().flat().join(',') : undefined
      const assetsPage = await entityService.getAll({
        'labels.type': 'ASSET',
        'path.main.or': assetPath,
        'point.within.bbox': viewportString,
        size: 1000
      })

      // Extract the unique subcategory IDs from each asset's BELONGS_TO relation.
      const categoryIds = [
        ...new Set(
          assetsPage.content
            .map(
              (entity) =>
                (entity.components as Component[]).find(
                  (c) => c.type === 'relation' && c['relationType'] === 'BELONGS_TO'
                )?.['targetId'] as string | undefined
            )
            .filter((id): id is string => Boolean(id))
        )
      ]

      if (categoryIds.length === 0) return []

      // Resolve IDs against the in-memory store (populated by fetchAll).
      const found = categoryIds
        .map((id) => base.categoriesById.value.get(id))
        .filter((cat): cat is Category => !!cat)

      // Fall back to an API fetch for any IDs not yet in the store.
      const foundIds = new Set(found.map((c) => c.id))
      const missingIds = categoryIds.filter((id) => !foundIds.has(id))
      if (missingIds.length > 0) {
        const missing = await entityService
          .getAll({ id: missingIds, 'labels.type': 'CATEGORY', size: 1000 })
          .then((p) => p.content.map(entityToCategory))
        initialCategories = [...found, ...missing]
      } else {
        initialCategories = found
      }
    } else {
      // Fetch selected categories by their IDs.
      initialCategories = await entityService
        .getAll({
          id: ids?.length ? ids : undefined,
          'labels.type': 'CATEGORY',
          size: 1000
        })
        .then((pageable) => pageable.content.map(entityToCategory))
    }

    // Enrichment: add ancestor categories for every leaf category found.
    const pathsWithParents = initialCategories
      .map((cat) => cat.path)
      .filter((path): path is string => !!path && path.includes('.'))

    const uniquePaths = new Set(pathsWithParents)
    if (uniquePaths.size === 0) return initialCategories

    const allParents = Array.from(uniquePaths).flatMap((path) => base.getParentsByPath(path))

    // Deduplicate and combine
    const uniqueCategories = new Map<string, Category>()
    for (const category of [...initialCategories, ...allParents]) {
      if (!uniqueCategories.has(category.id)) {
        uniqueCategories.set(category.id, category)
      }
    }

    return Array.from(uniqueCategories.values())
  }

  // Same TEST/PROD detection as IndoorviewerView.vue's NAVVIS label lookup —
  // no NavVis instance may be loaded yet if the category manager was opened
  // outside indoor view, in which case this falls back to PROD. Exposed so
  // CategoryFormModal.vue can show admins the detected value as the default
  // for their override toggle.
  const detectedNavVisEnv = computed<'PROD' | 'TEST'>(() => {
    const navvisInstanceURL = vuexStore?.getters?.['iv/navvisInstanceURL']
    return navvisInstanceURL?.includes('zynkatest.iv.navvis.com') ? 'TEST' : 'PROD'
  })

  async function createCategory(
    name: string,
    parentCategory?: Category,
    color?: string,
    envOverride?: 'PROD' | 'TEST'
  ): Promise<Category> {
    const env = envOverride ?? detectedNavVisEnv.value

    const res = await HTTPGatewayClient.post<{
      id: string
      name: string
      path: string
      color?: string
    }>(`/poi/assets/categories?syncToNavVis=${env}`, {
      name,
      parentId: parentCategory?.id ?? null,
      ...(color ? { color } : {})
    })

    const { id, name: resName, path, color: resColor } = res.data
    const segments = path?.split('.') ?? [id]
    const newCategory: Category = {
      id,
      name: resName,
      path,
      level: segments.length,
      parentId: segments.length > 1 ? segments[segments.length - 2] : undefined,
      color: resColor
    }

    const newById = new Map(base.categoriesById.value)
    newById.set(id, newCategory)
    base.categoriesById.value = newById

    if (path) {
      const newByPath = new Map(base.categoriesByPath.value)
      newByPath.set(path, newCategory)
      base.categoriesByPath.value = newByPath
    }

    return newCategory
  }

  async function updateCategory(
    id: string,
    name: string,
    parentId?: string | null,
    color?: string
  ): Promise<Category> {
    const res = await HTTPGatewayClient.patch<{
      id: string
      name: string
      path: string
      color?: string
    }>(`/poi/assets/categories/${id}`, {
      name,
      parentId: parentId ?? null,
      ...(color === undefined ? {} : { color })
    })

    const { name: resName, path, color: resColor } = res.data
    const existing = base.categoriesById.value.get(id)
    const oldPath = existing?.path
    const segments = path?.split('.') ?? []
    const updatedCategory: Category = {
      id,
      name: resName,
      path: path ?? existing?.path,
      level: segments.length || existing?.level,
      parentId: segments.length > 1 ? segments[segments.length - 2] : undefined,
      color: resColor ?? existing?.color
    }

    const newById = new Map(base.categoriesById.value)
    newById.set(id, updatedCategory)

    const newByPath = new Map(base.categoriesByPath.value)
    if (updatedCategory.path) {
      if (oldPath && oldPath !== updatedCategory.path) {
        newByPath.delete(oldPath)
        base.cascadeDescendantPaths(newById, newByPath, oldPath, updatedCategory.path, id)
      }
      newByPath.set(updatedCategory.path, updatedCategory)
    }

    base.categoriesById.value = newById
    base.categoriesByPath.value = newByPath

    return updatedCategory
  }

  async function deleteCategory(id: string): Promise<void> {
    await HTTPGatewayClient.delete(`/poi/assets/categories/${id}`)
    base.removeCategorySubtree(id)
  }

  async function getDeletePreview(
    id: string
  ): Promise<{ subcategoryCount: number; assetCount: number }> {
    const existing = base.categoriesById.value.get(id)
    if (!existing) return { subcategoryCount: 0, assetCount: 0 }

    const descendantIds: string[] = []
    for (const [catId, cat] of base.categoriesById.value) {
      if (catId !== id && existing.path && cat.path?.startsWith(existing.path + '.')) {
        descendantIds.push(catId)
      }
    }

    const allCategoryIds = [id, ...descendantIds]
    const entityService = useEntityService()
    const result = await entityService.getAll({
      'labels.type': 'ASSET',
      'relations.target.id': allCategoryIds,
      size: 1
    })

    return { subcategoryCount: descendantIds.length, assetCount: result.totalElements }
  }

  return {
    isLoading: readonly(base.isLoading),
    isLoaded: readonly(base.isLoaded),
    allCategories: base.allCategories,
    detectedNavVisEnv,
    fetchAll,
    createCategory,
    updateCategory,
    deleteCategory,
    getDeletePreview,
    getAll: base.getAll,
    getAllWithAssets,
    get: base.get,
    getParents: base.getParentsByPath,
    getParentsById,
    clearCache: base.clearCache
  }
})
